import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { request } from '../lib/api';
import { CustomerNavigationIcon } from '../components/CustomerNavigationIcon';
import { TapActionIcon } from '../features/tap-builder/TapActionIcon';
import { getActionLabel, type TapAction } from '../lib/tapDocument';
import { normalizePlanValue } from '../lib/subscriptionPlan';
import { useSession } from '../session';
import { formatDate, formatPlan, formatStatus } from './portalTypes';
import type { DashboardSummary as BaseDashboardSummary } from './portalTypes';

type PrimaryActionId =
  | 'build_tap_page'
  | 'finish_tap_page'
  | 'share_tap_link'
  | 'finish_card_checkout'
  | 'track_card_order'
  | 'view_analytics'
  | 'design_card';

type DashboardSummary = BaseDashboardSummary & {
  primaryAction: {
    id: PrimaryActionId;
    label: string;
    href: string;
  };
};

const profileInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const summaryActionIconType = (type: string): TapAction['type'] => {
  switch (type) {
    case 'download-card': case 'booking': case 'service-book': case 'website':
    case 'social': case 'file': case 'custom':
      return type;
    default:
      return 'custom';
  }
};

const primaryActionMessage = (summary: DashboardSummary) => {
  switch (summary.primaryAction.id) {
    case 'build_tap_page':
      return { title: 'Create your tap page', description: 'Add your profile and the actions people should see when they tap your card.' };
    case 'finish_tap_page':
      return { title: 'Finish your tap page', description: 'Review your profile and actions, then publish when you are ready.' };
    case 'share_tap_link':
      return { title: 'Share your tap page', description: 'Find the available sharing options for your tap page.' };
    case 'finish_card_checkout':
      return { title: 'Complete your card order', description: 'Continue checkout when you are ready to place your order.' };
    case 'track_card_order':
      if (summary.card.status === 'processing') {
        return { title: 'Your card is in production', description: 'Follow your card order progress from your orders.' };
      }
      if (summary.card.status === 'shipped') {
        return { title: 'Your card has shipped', description: 'Review your order and any available shipment details.' };
      }
      return { title: 'Track your card order', description: 'Review your latest order and its current status.' };
    case 'view_analytics':
      return { title: 'See how your card is performing', description: 'Review the analytics available for your tap page.' };
    case 'design_card':
      return { title: 'Design your card', description: 'Create or update the physical card that connects people to your tap page.' };
    default:
      return { title: 'Your InkTap workspace', description: 'Your tap page and card details are ready whenever you need them.' };
  }
};

function DashboardPage() {
  const { session, setSession } = useSession();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [failedCardPreview, setFailedCardPreview] = useState<string | null>(null);
  const [failedOrderPreview, setFailedOrderPreview] = useState<string | null>(null);
  const [failedTapPhoto, setFailedTapPhoto] = useState<string | null>(null);
  const requestVersion = useRef(0);
  const focusAfterLoad = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const loadDashboard = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setSummary(null);
    setError(null);
    setFailedCardPreview(null);
    setFailedOrderPreview(null);
    setFailedTapPhoto(null);
    try {
      const data = await request<DashboardSummary>('/me/dashboard');
      if (version !== requestVersion.current) return;
      setSummary(data);
      setSession((previous) =>
        previous
          ? {
              ...previous,
              email: data.user.email,
              publicSlug: data.tapPage?.publicSlug ?? previous.publicSlug,
              indexable: data.tapPage?.indexable ?? previous.indexable,
              active: data.tapPage?.active ?? previous.active,
              subscriptionPlan: normalizePlanValue(data.plan.plan) ?? null,
              subscriptionStatus: data.plan.status,
              canSyncToCloud: data.plan.entitlements.canSyncToCloud,
              entitlements: data.plan.entitlements,
              limits: data.plan.limits
            }
          : previous
      );
    } catch (err: any) {
      if (version === requestVersion.current) setError(err?.data?.message ?? 'Unable to load your dashboard.');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [setSession]);

  useEffect(() => {
    void loadDashboard();
    return () => { requestVersion.current += 1; };
  }, [loadDashboard]);

  useEffect(() => {
    if (loading || !focusAfterLoad.current) return;
    focusAfterLoad.current = false;
    if (error) errorRef.current?.focus();
    else headingRef.current?.focus();
  }, [loading, error, summary]);

  const refreshDashboard = () => {
    focusAfterLoad.current = true;
    void loadDashboard();
  };

  const actionMessage = useMemo(() => (summary ? primaryActionMessage(summary) : null), [summary]);
  const designPreview = summary?.card.latestDesign?.previewImageUrl ?? null;
  const orderPreview = summary?.card.latestOrder?.previewImageUrl ?? null;
  const showDesignPreview = Boolean(designPreview && failedCardPreview !== designPreview);
  const usableOrderPreview = orderPreview && failedOrderPreview !== orderPreview ? orderPreview : null;
  const cardPreview = showDesignPreview ? designPreview : usableOrderPreview;

  if (!session) {
    return null;
  }

  return (
    <div className="dashboard-page dashboard-page--home portal-workspace">
      {error ? (
        <section className="dashboard-recovery">
          <h1>Dashboard unavailable</h1>
          <div className="alert" role="alert" ref={errorRef} tabIndex={-1}>{error}</div>
          <button type="button" className="button-secondary" onClick={refreshDashboard}>Retry dashboard</button>
        </section>
      ) : null}

      {loading ? (
        <section className="dashboard-section" aria-live="polite">
          <p className="helper-text">Loading your workspace...</p>
        </section>
      ) : summary && actionMessage ? (
        <div className="dashboard-home dashboard-home--action">
          <header className="portal-intro portal-intro--action">
            <div>
              <h1 ref={headingRef} tabIndex={-1}>Welcome back.</h1>
              <p>Your introduction, all in one place.</p>
            </div>
            <div className="dashboard-next-action">
              <div>
                <h2>{actionMessage.title}</h2>
                <p>{actionMessage.description}</p>
              </div>
              <Link className="btn-primary portal-primary-action" to={summary.primaryAction.href}>
                {summary.primaryAction.label}
              </Link>
            </div>
          </header>

          <div className="customer-overview">
            <section className="customer-overview__card" aria-labelledby="customer-card-title">
              <h2 id="customer-card-title">Your business card</h2>
              <div className="customer-overview__card-stage">
                {cardPreview ? (
                  <figure className="customer-overview__card-figure">
                    <img src={cardPreview} alt={showDesignPreview ? 'Stored card image' : 'Your latest order card preview'}
                      onError={() => showDesignPreview ? setFailedCardPreview(cardPreview) : setFailedOrderPreview(cardPreview)} />
                    <figcaption>{showDesignPreview ? 'Stored card image. It may not reflect your current edits.' : 'Latest order preview'}</figcaption>
                  </figure>
                ) : (
                  <div className="customer-overview__empty">
                    <span className="brand-wordmark"><span className="brand-wordmark__ink">ink</span><span className="brand-wordmark__tap">tap</span></span>
                    <p>{designPreview || orderPreview ? 'Card preview unavailable. Refresh to try again.' : 'No card preview is available yet.'}</p>
                  </div>
                )}
              </div>
              <div className="customer-overview__actions">
                <Link className="btn-primary" to="/card-design">Manage your card</Link>
                <Link className="button-secondary" to="/orders">View orders</Link>
              </div>
            </section>
            <section className="customer-overview__tap" aria-labelledby="customer-tap-title">
              <div className="customer-overview__tap-head"><h2 id="customer-tap-title">Your Tap Page</h2>
                {summary.tapPage?.links.selfView ? <a href={summary.tapPage.links.selfView}>Preview page</a> : null}
              </div>
              {summary.tapPage?.preview ? (
                <div className="customer-overview__phone" aria-label="Tap Page summary" data-theme={summary.tapPage.preview.theme}>
                  <span className="customer-overview__phone-brand brand-wordmark"><span className="brand-wordmark__ink">ink</span><span className="brand-wordmark__tap">tap</span></span>
                  {summary.tapPage.preview.profile.photoUrl && failedTapPhoto !== summary.tapPage.preview.profile.photoUrl ? (
                    <img src={summary.tapPage.preview.profile.photoUrl} alt=""
                      onError={() => setFailedTapPhoto(summary.tapPage!.preview!.profile.photoUrl!)} />
                  ) : <span className="customer-overview__phone-initials" aria-hidden="true">{profileInitials(summary.tapPage.preview.profile.name)}</span>}
                  <strong>{summary.tapPage.preview.profile.name}</strong>
                  <span>{summary.tapPage.preview.profile.title}</span>
                  {summary.tapPage.preview.profile.subtitle ? <span>{summary.tapPage.preview.profile.subtitle}</span> : null}
                  <ul aria-label="Tap Page actions">{summary.tapPage.preview.actions.slice(0, 4).map(action => (
                    <li key={action.id}><TapActionIcon type={summaryActionIconType(action.type)} />
                      <span>{getActionLabel({ ...action, type: action.type as TapAction['type'], label: action.label ?? undefined, provider: (action.provider ?? undefined) as TapAction['provider'] })}</span>
                    </li>
                  ))}</ul>
                  {summary.tapPage.preview.actions.length > 4 ? <small>And {summary.tapPage.preview.actions.length - 4} more actions</small> : null}
                </div>
              ) : <div className="customer-overview__empty customer-overview__tap-empty"><p>Your Tap Page preview will appear here as you add your details.</p></div>}
              <Link className="btn-primary" to="/tap">Manage Tap Page</Link>
            </section>
          </div>

          <div className="customer-overview__secondary">
          <section className="customer-overview__recent-order" aria-labelledby="latest-order-title">
            <div className="customer-overview__recent-heading">
              <h2 id="latest-order-title">Latest order</h2>
              <Link to="/orders">View all orders</Link>
            </div>
            {summary.card.latestOrder ? (
              <div className="customer-overview__order-summary">
                <div className="customer-overview__order-image">
                  {orderPreview && failedOrderPreview !== orderPreview ? (
                    <img src={orderPreview} alt="" onError={() => setFailedOrderPreview(orderPreview)} />
                  ) : <span>Preview unavailable</span>}
                </div>
                <div className="customer-overview__order-details">
                  <strong>{summary.card.latestOrder.material} cards · {summary.card.latestOrder.quantity}</strong>
                  <span className="customer-overview__order-status">{formatStatus(summary.card.latestOrder.status)}</span>
                  <p>Order {summary.card.latestOrder.id}</p>
                  <p>Placed {formatDate(summary.card.latestOrder.createdAt)}</p>
                </div>
              </div>
            ) : (
              <p className="customer-overview__order-empty">Your latest card order will appear here once you place one.</p>
            )}
          </section>
          <section className="customer-overview__card-status" aria-labelledby="customer-card-status-title">
            <h2 id="customer-card-status-title">Card design</h2>
            {summary.card.latestDesign ? <>
              <strong>Design record available</strong>
              <p>Style: {summary.card.latestDesign.styleId}</p>
              <p>Updated {formatDate(summary.card.latestDesign.updatedAt)}</p>
            </> : <p>No card design record yet.</p>}
            <Link to="/card-design">{summary.card.latestDesign ? 'Manage your card' : 'Design card'}</Link>
          </section>
          <nav className="customer-overview__quick-links" aria-label="Quick links">
            <h2>Quick links</h2>
            <div className="customer-overview__quick-list">
              <Link to="/tap"><CustomerNavigationIcon to="/tap" className="customer-overview__quick-icon" /><span>Edit Tap Page</span><span className="customer-overview__quick-arrow" aria-hidden="true">→</span></Link>
              <Link to="/card-design"><CustomerNavigationIcon to="/card-design" className="customer-overview__quick-icon" /><span>Manage your card</span><span className="customer-overview__quick-arrow" aria-hidden="true">→</span></Link>
              <Link to="/orders"><CustomerNavigationIcon to="/orders" className="customer-overview__quick-icon" /><span>View orders</span><span className="customer-overview__quick-arrow" aria-hidden="true">→</span></Link>
              <Link to="/contacts"><CustomerNavigationIcon to="/contacts" className="customer-overview__quick-icon" /><span>Manage contacts</span><span className="customer-overview__quick-arrow" aria-hidden="true">→</span></Link>
              <Link to="/analytics"><CustomerNavigationIcon to="/analytics" className="customer-overview__quick-icon" /><span>View analytics</span><span className="customer-overview__quick-arrow" aria-hidden="true">→</span></Link>
              <Link to="/plan-billing"><CustomerNavigationIcon to="/plan-billing" className="customer-overview__quick-icon" /><span>Plan &amp; billing</span><span className="customer-overview__quick-arrow" aria-hidden="true">→</span></Link>
              <Link to="/settings"><CustomerNavigationIcon to="/settings" className="customer-overview__quick-icon" /><span>Account settings</span><span className="customer-overview__quick-arrow" aria-hidden="true">→</span></Link>
            </div>
          </nav>
          </div>

          <section className="workspace-status-rail" aria-label="Workspace status">
            <article>
              <span>Tap page</span>
              <strong>{summary.tapPage?.status ? formatStatus(summary.tapPage.status) : 'Not started'}</strong>
              <p>{summary.tapPage?.status === 'published' ? `Live since ${formatDate(summary.tapPage.publishedAt)}` : 'Build the page people see after a tap.'}</p>
              <Link to="/tap">View page</Link>
            </article>
            <article>
              <span>Card</span>
              <strong>{formatStatus(summary.card.status)}</strong>
              <p>{summary.card.latestOrder ? `${summary.card.latestOrder.material} · Quantity ${summary.card.latestOrder.quantity}` : 'No physical card order yet.'}</p>
              <Link to={summary.card.latestOrder ? '/orders' : '/card-design'}>
                {summary.card.latestOrder ? 'View order' : 'Design card'}
              </Link>
            </article>
            <article>
              <span>Plan</span>
              <strong>{formatPlan(summary.plan.plan)}</strong>
              <p>{summary.plan.entitlements.canUseAnalytics ? 'Tap analytics are included.' : 'Core tap-page tools are included.'}</p>
              <Link to="/plan-billing">Manage plan</Link>
            </article>
          </section>

          <section className="activity-panel">
            <div className="activity-panel__head">
              <div>
                <h2>Recent activity</h2>
                <p>The latest changes across your page and card orders.</p>
              </div>
              <button type="button" className="section-refresh" onClick={refreshDashboard}>
                Refresh
              </button>
            </div>
            {summary.recentActivity.length > 0 ? (
              <ol className="dashboard-activity-list">
                {summary.recentActivity.map((item) => (
                  <li key={item.id}>
                    <span>{item.label}</span>
                    <time>{formatDate(item.occurredAt)}</time>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="helper-text">Your recent page and order updates will appear here.</p>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default DashboardPage;
